/**
 * `?inkdebug=1` -- an on-screen readout of the ink metrics for the iPad, which
 * has no devtools. Imperative DOM (one fixed <div> under body, no React), so
 * turning it on cannot change what the viewer renders or when.
 *
 * `?inkdebug=1` turns it on and remembers it (localStorage, like the
 * `pmt.input.*` hold settings); `?inkdebug=0` or the panel's "Turn off" turns it off again.
 */
import type { InkStrokeSummary } from './inkMetrics';

const STORAGE_KEY = 'pmt.input.inkdebug';

export function inkDebugEnabled(): boolean {
    try {
        const q = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('inkdebug') : null;
        if (q === '1') localStorage.setItem(STORAGE_KEY, '1');
        else if (q === '0' || q === '') localStorage.removeItem(STORAGE_KEY);
        return localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
        return false;
    }
}

export interface InkLiveState {
    pointerType: string;
    lastLatencyMs: number;
    coalesced: number;
    predicted: number;
    pressure: number | null;
    hover: boolean;
}

let panel: HTMLDivElement | null = null;
let text: HTMLDivElement | null = null;
let lastStroke: InkStrokeSummary | null = null;
let strokes = 0;
let context: string[] = [];
let counters = '';
// The input log (core/ink/inputLog.ts), when it is installed: its tail is shown, "Copy log" hands it over.
let inputLog: readonly object[] | null = null;
// What Copy log hands over (`{header, rows}`, core/ink/inputLog.ts); null = the bare ring.
let copySource: (() => object) | null = null;
let lastLive: InkLiveState | null = null;
let redrawQueued = false;

function ensurePanel(): HTMLDivElement | null {
    if (typeof document === 'undefined') return null;
    if (panel && panel.isConnected) return panel;
    panel = document.createElement('div');
    text = document.createElement('div');
    panel.appendChild(text);
    panel.setAttribute('data-testid', 'ink-debug-overlay');
    panel.setAttribute('aria-hidden', 'true');
    Object.assign(panel.style, {
        position: 'fixed',
        left: '8px',
        bottom: '8px',
        zIndex: '2147483001',
        pointerEvents: 'none',
        background: 'var(--color-scrim)',
        color: 'var(--text-on-scrim)',
        font: '11px/1.35 ui-monospace, Menlo, monospace',
        padding: '6px 8px',
        borderRadius: '6px',
        whiteSpace: 'pre',
        maxWidth: '70vw',
    } satisfies Partial<CSSStyleDeclaration>);
    document.body.appendChild(panel);
    return panel;
}

/** Static facts shown on every redraw (Safari version, desync state...). */
export function setInkDebugContext(lines: string[]): void {
    context = lines;
}

/**
 * The one-line counters (hover reports seen, predicted points drawn this
 * stroke, replayed samples dropped) -- the user's way to confirm on the iPad
 * that hover and the predicted tail are really happening.
 */
export function setInkCounters(line: string): void {
    counters = line;
}

function fmt(s: InkStrokeSummary): string[] {
    return [
        `stroke #${strokes} ${s.pointer_type}  ${s.move_events} moves / ${s.samples} samples / ${s.duration_ms} ms`,
        `latency p50 ${s.latency_p50_ms} ms  p95 ${s.latency_p95_ms} ms  max ${s.latency_max_ms} ms`,
        `rate ${s.sample_rate_hz} Hz  coalesced/move avg ${s.coalesced_per_move_avg} max ${s.coalesced_per_move_max}`,
        `predicted/move ${s.predicted_per_move_avg} (${s.predicted_available ? 'available' : 'NONE'}) drawn ${s.predicted_drawn}  pressure ${s.pressure_min}..${s.pressure_max}`,
        `replays dropped ${s.replayed_samples}  hover reports before stroke ${s.hover_reports}`,
    ];
}

/** One log entry as a short line for the panel (the full JSON goes to Copy log). */
function brief(entry: object): string {
    const { t, k, ...rest } = entry as { t?: number; k?: string } & Record<string, unknown>;
    return `${t ?? ''} ${k ?? ''} ${JSON.stringify(rest)}`.slice(0, 140);
}

/**
 * "Copy log": the input log as JSON to the clipboard; where the clipboard is refused (no permission,
 * not a secure context) a selectable textarea holding the same JSON opens in the panel instead.
 */
function copyLog(button: HTMLButtonElement): void {
    const json = JSON.stringify(copySource ? copySource() : inputLog ?? [], null, 1);
    const fallback = () => {
        const p = ensurePanel();
        if (!p) return;
        let area = p.querySelector<HTMLTextAreaElement>('[data-testid="ink-debug-log-text"]');
        if (!area) {
            area = document.createElement('textarea');
            area.setAttribute('data-testid', 'ink-debug-log-text');
            area.readOnly = true;
            Object.assign(area.style, { display: 'block', width: '60vw', height: '30vh', pointerEvents: 'auto', fontSize: '11px' });
            p.appendChild(area);
        }
        area.value = json;
        area.focus();
        area.select();
        button.textContent = 'Copy log (select the text)';
    };
    try {
        const clip = typeof navigator !== 'undefined' ? navigator.clipboard : undefined;
        if (!clip?.writeText) return fallback();
        clip.writeText(json).then(() => { button.textContent = `Copied ${inputLog?.length ?? 0}`; }, fallback);
    } catch {
        fallback();
    }
}

// Collapsed: the panel shows one line and its buttons, so it never covers a sheet's controls (the Media sheet,
// the review bottom sheet). Remembered for the tab (sessionStorage) so a reload keeps it out of the way.
const COLLAPSED_KEY = 'pmt.inkdebug.collapsed';
function readCollapsed(): boolean {
    try {
        return sessionStorage.getItem(COLLAPSED_KEY) === '1';
    } catch {
        return false;
    }
}
let collapsed = readCollapsed();

/** The panel's controls row: "Hide" / "Show" (always) and "Copy log" (once an input log exists). */
function ensureButtons(el: HTMLDivElement, withCopy: boolean): void {
    // Not React controls: this panel is imperative DOM by design (it must not touch rendering).
    let row = el.querySelector<HTMLDivElement>('[data-testid="ink-debug-buttons"]');
    if (!row) {
        row = document.createElement('div');
        row.setAttribute('data-testid', 'ink-debug-buttons');
        Object.assign(row.style, { display: 'flex', gap: '8px', marginTop: '4px' });
        const toggle = document.createElement('button');
        toggle.type = 'button';
        toggle.setAttribute('data-testid', 'ink-debug-toggle');
        Object.assign(toggle.style, { pointerEvents: 'auto', minHeight: '44px', minWidth: '88px', font: 'inherit' });
        toggle.addEventListener('click', () => {
            collapsed = !collapsed;
            try {
                sessionStorage.setItem(COLLAPSED_KEY, collapsed ? '1' : '0');
            } catch {
                /* private mode: in-memory only */
            }
            render(lastLive);
        });
        row.appendChild(toggle);
        // ?inkdebug=1 is sticky (localStorage) -- the panel must offer its own way out, not only ?inkdebug=0.
        const off = document.createElement('button');
        off.type = 'button';
        off.textContent = 'Turn off';
        off.setAttribute('data-testid', 'ink-debug-off');
        Object.assign(off.style, { pointerEvents: 'auto', minHeight: '44px', minWidth: '88px', font: 'inherit' });
        off.addEventListener('click', () => {
            try {
                localStorage.removeItem(STORAGE_KEY);
            } catch {
                /* nothing stored */
            }
            window.location.reload(); // the input log and its listeners are installed at load: a reload removes them
        });
        row.appendChild(off);
        el.appendChild(row);
    }
    const toggle = row.querySelector<HTMLButtonElement>('[data-testid="ink-debug-toggle"]');
    if (toggle) toggle.textContent = collapsed ? 'Show' : 'Hide';
    if (withCopy && !row.querySelector('[data-testid="ink-debug-copy-log"]')) {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = 'Copy log';
        button.setAttribute('data-testid', 'ink-debug-copy-log');
        Object.assign(button.style, { pointerEvents: 'auto', minHeight: '44px', minWidth: '88px', font: 'inherit' });
        button.addEventListener('click', () => copyLog(button));
        row.appendChild(button);
    }
}

/** What Copy log hands over (core/ink/inputLog.ts installs `{header, rows}`; null: the bare ring). */
export function setInkLogCopySource(source: (() => object) | null): void {
    copySource = source;
}

/**
 * The newest `edit` row as one line: `last edit: <op> <type> <cause>: raw -> stored (dx, dy px)` -- the moved point
 * (`moved`, core/ink/outcomeLog.ts) when the edit has one.
 */
function lastEditLine(log: readonly object[]): string | null {
    for (let i = log.length - 1; i >= 0; i--) {
        const e = log[i] as { k?: string; op?: string; type?: string; cause?: string; handle?: number; moved?: { from: Pt; to: Pt; dxPx: number; dyPx: number } };
        if (e.k !== 'edit') continue;
        const head = `last edit: ${e.op ?? '?'} ${e.type ?? '?'} ${e.cause ?? '?'}${typeof e.handle === 'number' ? ` h${e.handle}` : ''}`;
        const m = e.moved;
        return m ? `${head}: ${pt(m.from)} -> ${pt(m.to)} (${m.dxPx}, ${m.dyPx} px)` : head;
    }
    return null;
}
type Pt = { x: number; y: number } | null;
const pt = (p: Pt) => (p ? `(${p.x}, ${p.y})` : '-');

/** The input log changed (core/ink/inputLog.ts): redraw its tail, at most once a frame (`now`: at once). */
export function setInkInputLog(log: readonly object[], now = false): void {
    inputLog = log;
    if (now) return render(lastLive);
    if (redrawQueued) return;
    redrawQueued = true;
    const later = typeof requestAnimationFrame === 'function' ? requestAnimationFrame : (cb: () => void) => setTimeout(cb, 16);
    later(() => {
        redrawQueued = false;
        render(lastLive);
    });
}

function render(live: InkLiveState | null): void {
    lastLive = live;
    const el = ensurePanel();
    if (!el || !text) return;
    const lines = ['ink debug', ...context];
    if (counters) lines.push(counters);
    if (live) {
        lines.push(`live ${live.hover ? 'hover' : 'contact'} ${live.pointerType}  lat ${live.lastLatencyMs.toFixed(1)} ms  coal ${live.coalesced}  pred ${live.predicted}  p ${live.pressure === null ? '-' : live.pressure.toFixed(2)}`);
    }
    if (lastStroke) lines.push(...fmt(lastStroke));
    else lines.push('draw a stroke...');
    if (inputLog) {
        const edit = lastEditLine(inputLog);
        lines.push(`input log ${inputLog.length} rows`, ...inputLog.slice(-4).map(brief));
        if (edit) lines.push(edit);
    }
    ensureButtons(el, inputLog !== null);
    text.textContent = collapsed ? `ink debug (hidden)${inputLog ? `  log ${inputLog.length} rows` : ''}` : lines.join('\n');
}

export function showInkLive(live: InkLiveState): void {
    render(live);
}

export function showInkStroke(summary: InkStrokeSummary): void {
    strokes++;
    lastStroke = summary;
    render(null);
}

/** Test hook: forget the panel and counters. */
export function resetInkDebugOverlay(): void {
    panel?.remove();
    panel = null;
    collapsed = readCollapsed();
    text = null;
    inputLog = null;
    copySource = null;
    lastLive = null;
    redrawQueued = false;
    lastStroke = null;
    strokes = 0;
    context = [];
    counters = '';
}
